import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useInfiniteQuery } from '@tanstack/react-query';
import { createColumnHelper, flexRender, getCoreRowModel, useReactTable, type RowSelectionState, type VisibilityState } from '@tanstack/react-table';
import { AlertOctagon, Archive, ArchiveRestore, Bot, Columns3, LayoutList, Plus, Search, SlidersHorizontal, TableProperties, X } from 'lucide-react';
import type { WorkItemListItem } from '@vwt/contracts';
import {
  Badge,
  Button,
  Checkbox,
  cn,
  Drawer,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  EmptyState,
  Input,
  NativeSelect,
  toast,
} from '@vwt/ui';
import { api, newIdempotencyKey, POLL_ACTIVE, useApiMutation, useApiQuery } from '../../lib/api.js';
import { toastError } from '../../lib/errors.js';
import { useTranslation } from '../../lib/i18n.js';
import { hasUnsavedChanges } from '../../lib/unsaved.js';
import { DeliveryBadge, KeyLabel, PhaseBadge, PriorityLabel } from '../../components/badges.js';
import { PageHeader, PrincipalPicker, QueryState, TimeAgo, UserChip } from '../../components/common.js';
import { SavedViewsMenu } from '../../components/saved-views.js';
import { ExportMenu } from '../../components/export-menu.js';
import { CreateWorkDialog } from './create-work.js';
import { WorkDetail } from './work-detail.js';
import { WorkBoard } from './work-board.js';

const FILTER_KEYS = ['q', 'phase', 'type', 'priority', 'assigneeId', 'projectId', 'teamId', 'blocked', 'associationStatus', 'includeCanceled', 'archived', 'label', 'sort'] as const;

export function WorkListPage() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const view = params.get('view') === 'board' ? 'board' : 'list';
  const selected = params.get('selected');
  const [search, setSearch] = useState(params.get('q') ?? '');
  const filters = useMemo(() => Object.fromEntries(FILTER_KEYS.map((k) => [k, params.get(k) ?? undefined]).filter(([, v]) => v)) as Record<string, string>, [params]);

  const setParam = (key: string, value: string | null) => {
    setParams(
      (p) => {
        const next = new URLSearchParams(p);
        if (value) next.set(key, value);
        else next.delete(key);
        return next;
      },
      { replace: key === 'q' },
    );
  };

  // Debounced search keeps typing responsive and the URL shareable (no protected content in URLs).
  useEffect(() => {
    // Only write when the text changed: an unconditional write on mount would replace newer params (e.g. create=1).
    if ((params.get('q') ?? '') === search.trim()) return;
    const id = setTimeout(() => setParam('q', search.trim() || null), 250);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const list = useInfiniteQuery({
    queryKey: ['listWorkItems', 'infinite', filters, view],
    queryFn: ({ pageParam, signal }) => api.call('listWorkItems', { query: { ...filters, limit: view === 'board' ? '100' : '50', ...(pageParam ? { cursor: pageParam } : {}) }, signal }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last?.meta.nextCursor ?? undefined,
    refetchInterval: POLL_ACTIVE,
  });
  const items = useMemo(() => list.data?.pages.flatMap((p) => p?.data ?? []) ?? [], [list.data]);

  const createOpen = params.get('create') === '1';
  const [columnVisibility, setColumnVisibility] = useColumnVisibility();
  const activeFilterCount = FILTER_KEYS.filter((k) => k !== 'q' && k !== 'sort' && params.get(k)).length;

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        title={t('work.title')}
        subtitle={t('work.subtitle')}
        actions={
          <>
            <div className="inline-flex rounded-[var(--radius)] border border-border-strong p-0.5" role="group" aria-label="View">
              <Button size="sm" variant={view === 'list' ? 'secondary' : 'ghost'} className={cn(view === 'list' && 'shadow-none')} onClick={() => setParam('view', null)} aria-pressed={view === 'list'}>
                <LayoutList /> {t('work.views.list')}
              </Button>
              <Button size="sm" variant={view === 'board' ? 'secondary' : 'ghost'} className={cn(view === 'board' && 'shadow-none')} onClick={() => setParam('view', 'board')} aria-pressed={view === 'board'}>
                <Columns3 /> {t('work.views.board')}
              </Button>
            </div>
            <Button variant="primary" onClick={() => setParam('create', '1')}>
              <Plus /> {t('nav.newWork')}
            </Button>
          </>
        }
      >
        <div className="flex flex-wrap items-center gap-2 pb-3">
          <div className="relative w-full max-w-xs">
            <Search className="pointer-events-none absolute top-2 left-2.5 size-4 text-fg-subtle" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t('nav.search')} className="pl-8" aria-label={t('nav.search')} />
          </div>
          <FilterMenu params={params} setParam={setParam} count={activeFilterCount} />
          <SavedViewsMenu
            scope="WORK"
            params={params}
            onApply={(query) => {
              setSearch(query.q ?? '');
              setParams(new URLSearchParams(query));
            }}
          />
          <ExportMenu path="/api/v1/work-items/export" filters={filters} />
          <QuickToggle active={params.get('assigneeId') === 'me'} onClick={() => setParam('assigneeId', params.get('assigneeId') === 'me' ? null : 'me')}>
            {t('common.me')}
          </QuickToggle>
          <QuickToggle active={params.get('blocked') === 'true'} onClick={() => setParam('blocked', params.get('blocked') === 'true' ? null : 'true')}>
            <AlertOctagon className="size-3.5" /> {t('work.filters.blocked')}
          </QuickToggle>
          <QuickToggle active={params.get('associationStatus') === 'PROVISIONAL'} onClick={() => setParam('associationStatus', params.get('associationStatus') === 'PROVISIONAL' ? null : 'PROVISIONAL')}>
            <Bot className="size-3.5" /> {t('work.filters.provisional')}
          </QuickToggle>
          {activeFilterCount ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() =>
                setParams((p) => {
                  const next = new URLSearchParams();
                  if (p.get('view')) next.set('view', p.get('view')!);
                  return next;
                })
              }
            >
              <X /> {t('common.clearFilters')}
            </Button>
          ) : null}
          <div className="ml-auto hidden md:block">{view === 'list' ? <ColumnsMenu visibility={columnVisibility} onChange={setColumnVisibility} /> : null}</div>
          <NativeSelect value={params.get('sort') ?? 'updatedAt'} onChange={(e) => setParam('sort', e.target.value === 'updatedAt' ? null : e.target.value)} className="ml-auto w-auto md:ml-0" aria-label={t('common.sort')}>
            <option value="updatedAt">{t('work.columns.updated')}</option>
            <option value="priority">{t('work.columns.priority')}</option>
            <option value="createdAt">{t('common.created')}</option>
            <option value="key">{t('work.columns.key')}</option>
          </NativeSelect>
        </div>
      </PageHeader>
      <div className="min-h-0 flex-1 overflow-auto">
        <QueryState query={list} empty={items.length === 0 ? <EmptyState icon={<LayoutList />} title={t('work.empty')} action={<Button onClick={() => setParam('create', '1')}><Plus /> {t('nav.newWork')}</Button>} /> : undefined}>
          {view === 'board' ? <WorkBoard items={items} onOpen={(id) => setParam('selected', id)} /> : <WorkTable items={items} selectedId={selected} onOpen={(id) => setParam('selected', id)} columnVisibility={columnVisibility} />}
          {list.hasNextPage ? (
            <div className="flex justify-center p-3">
              <Button variant="secondary" size="sm" onClick={() => void list.fetchNextPage()} loading={list.isFetchingNextPage}>
                {t('common.loadMore')}
              </Button>
            </div>
          ) : null}
        </QueryState>
      </div>
      <Drawer open={!!selected} onOpenChange={(open) => {
          if (open) return;
          if (hasUnsavedChanges()) {
            toast.warning(t('common.unsavedChanges'));
            return;
          }
          setParam('selected', null);
        }}
        onEscapeKeyDown={(event) => {
          if (!hasUnsavedChanges()) return;
          event.preventDefault();
          toast.warning(t('common.unsavedChanges'));
        }} title={t('work.title')} closeLabel={t('common.close')}>
        {selected ? <WorkDetail id={selected} inDrawer /> : null}
      </Drawer>
      <CreateWorkDialog open={createOpen} onOpenChange={(open) => setParam('create', open ? '1' : null)} />
    </div>
  );
}

function QuickToggle({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button type="button" onClick={onClick} aria-pressed={active} className={cn('inline-flex h-8 items-center gap-1 rounded-full border px-3 text-xs font-medium', active ? 'border-accent bg-accent-soft text-accent-soft-fg' : 'border-border-strong text-fg-muted hover:bg-muted')}>
      {children}
    </button>
  );
}

function FilterMenu({ params, setParam, count }: { params: URLSearchParams; setParam: (key: string, value: string | null) => void; count: number }) {
  const { t } = useTranslation();
  const projects = useApiQuery('listProjects', { query: {} });
  const teams = useApiQuery('listTeams', { query: {} });
  const toggleInList = (key: string, value: string) => {
    const current = new Set((params.get(key) ?? '').split(',').filter(Boolean));
    if (current.has(value)) current.delete(value);
    else current.add(value);
    setParam(key, [...current].join(',') || null);
  };
  const has = (key: string, value: string) => (params.get(key) ?? '').split(',').includes(value);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button size="md" variant="secondary">
          <SlidersHorizontal /> {t('common.filter')}
          {count ? <Badge tone="accent">{count}</Badge> : null}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="max-h-[70vh] w-64 overflow-y-auto">
        <DropdownMenuLabel>{t('work.filters.phase')}</DropdownMenuLabel>
        {(['TODO', 'IN_PROGRESS', 'IN_REVIEW', 'INTEGRATED', 'DONE', 'CANCELED'] as const).map((p) => (
          <DropdownMenuItem key={p} onSelect={(e) => (e.preventDefault(), toggleInList('phase', p))}>
            <Checkbox checked={has('phase', p)} aria-hidden tabIndex={-1} /> {t(`status.phase.${p}`)}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuLabel>{t('work.filters.priority')}</DropdownMenuLabel>
        {(['URGENT', 'HIGH', 'NORMAL', 'LOW'] as const).map((p) => (
          <DropdownMenuItem key={p} onSelect={(e) => (e.preventDefault(), toggleInList('priority', p))}>
            <Checkbox checked={has('priority', p)} aria-hidden tabIndex={-1} /> {t(`status.priority.${p}`)}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuLabel>{t('work.filters.type')}</DropdownMenuLabel>
        {(['FEATURE', 'BUG', 'MAINTENANCE', 'INFRASTRUCTURE', 'RESEARCH', 'ANALYSIS', 'OPERATIONS', 'REVIEW', 'OTHER'] as const).map((p) => (
          <DropdownMenuItem key={p} onSelect={(e) => (e.preventDefault(), toggleInList('type', p))}>
            <Checkbox checked={has('type', p)} aria-hidden tabIndex={-1} /> {t(`status.workType.${p}`)}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuLabel>{t('work.filters.project')}</DropdownMenuLabel>
        {(projects.data?.data ?? []).map((p) => (
          <DropdownMenuItem key={p.id} onSelect={(e) => (e.preventDefault(), setParam('projectId', params.get('projectId') === p.id ? null : p.id))}>
            <Checkbox checked={params.get('projectId') === p.id} aria-hidden tabIndex={-1} /> {p.name}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuLabel>{t('work.filters.team')}</DropdownMenuLabel>
        {(teams.data?.data ?? []).map((team) => (
          <DropdownMenuItem key={team.id} onSelect={(e) => (e.preventDefault(), setParam('teamId', params.get('teamId') === team.id ? null : team.id))}>
            <Checkbox checked={params.get('teamId') === team.id} aria-hidden tabIndex={-1} /> {team.name}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={(e) => (e.preventDefault(), setParam('includeCanceled', params.get('includeCanceled') === 'true' ? null : 'true'))}>
          <Checkbox checked={params.get('includeCanceled') === 'true'} aria-hidden tabIndex={-1} /> {t('work.filters.includeCanceled')}
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={(e) => (e.preventDefault(), setParam('archived', params.get('archived') === 'true' ? null : 'true'))}>
          <Checkbox checked={params.get('archived') === 'true'} aria-hidden tabIndex={-1} /> {t('work.filters.archived')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

const col = createColumnHelper<WorkItemListItem>();

/** Columns people can hide or show; the choice is a per-browser preference. */
const OPTIONAL_COLUMNS = ['phase', 'priority', 'type', 'assignee', 'owner', 'scope', 'team', 'labels', 'delivery', 'createdAt', 'updatedAt'] as const;
const DEFAULT_HIDDEN: VisibilityState = { type: false, owner: false, team: false, labels: false, createdAt: false };
const COLUMNS_STORAGE_KEY = 'vwt.work.columns';

function loadColumnVisibility(): VisibilityState {
  try {
    const stored = JSON.parse(localStorage.getItem(COLUMNS_STORAGE_KEY) ?? 'null') as VisibilityState | null;
    return stored && typeof stored === 'object' ? { ...DEFAULT_HIDDEN, ...stored } : DEFAULT_HIDDEN;
  } catch {
    return DEFAULT_HIDDEN;
  }
}

function useColumnVisibility() {
  const [visibility, setVisibility] = useState<VisibilityState>(loadColumnVisibility);
  const update = useCallback((next: VisibilityState) => {
    setVisibility(next);
    try {
      localStorage.setItem(COLUMNS_STORAGE_KEY, JSON.stringify(next));
    } catch {
      // Storage can be unavailable (private mode); the choice then lasts for this page only.
    }
  }, []);
  return [visibility, update] as const;
}

function ColumnsMenu({ visibility, onChange }: { visibility: VisibilityState; onChange: (next: VisibilityState) => void }) {
  const { t } = useTranslation();
  const labels: Record<(typeof OPTIONAL_COLUMNS)[number], string> = {
    phase: t('work.columns.phase'),
    priority: t('work.columns.priority'),
    type: t('work.columns.type'),
    assignee: t('work.columns.assignee'),
    owner: t('work.columns.owner'),
    scope: t('work.columns.project'),
    team: t('work.columns.team'),
    labels: t('work.columns.labels'),
    delivery: t('work.columns.delivery'),
    createdAt: t('work.columns.created'),
    updatedAt: t('work.columns.updated'),
  };
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button size="iconSm" variant="ghost" aria-label={t('work.columnsMenu.title')} title={t('work.columnsMenu.title')}>
          <TableProperties />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>{t('work.columnsMenu.title')}</DropdownMenuLabel>
        {OPTIONAL_COLUMNS.map((id) => (
          <DropdownMenuItem key={id} onSelect={(e) => (e.preventDefault(), onChange({ ...visibility, [id]: visibility[id] === false }))}>
            <Checkbox checked={visibility[id] !== false} aria-hidden tabIndex={-1} /> {labels[id]}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => onChange(DEFAULT_HIDDEN)}>{t('work.columnsMenu.reset')}</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function WorkTable({ items, selectedId, onOpen, columnVisibility }: { items: WorkItemListItem[]; selectedId: string | null; onOpen: (id: string) => void; columnVisibility: VisibilityState }) {
  const { t } = useTranslation();
  const [selection, setSelection] = useState<RowSelectionState>({});
  const columns = useMemo(
    () => [
      col.display({
        id: 'select',
        header: ({ table }) => <Checkbox checked={table.getIsAllRowsSelected() ? true : table.getIsSomeRowsSelected() ? 'indeterminate' : false} onCheckedChange={(v) => table.toggleAllRowsSelected(v === true)} aria-label="Select all" />,
        cell: ({ row }) => <Checkbox checked={row.getIsSelected()} onCheckedChange={(v) => row.toggleSelected(v === true)} onClick={(e) => e.stopPropagation()} aria-label={`Select ${row.original.key}`} />,
      }),
      col.accessor('key', { header: t('work.columns.key'), cell: (c) => <KeyLabel value={c.getValue()} /> }),
      col.accessor('title', {
        header: t('work.columns.title'),
        cell: (c) => (
          <div className="flex min-w-0 items-center gap-2">
            {c.row.original.blocked ? <AlertOctagon className="size-3.5 shrink-0 text-danger" aria-label={t('work.detail.blockedNote')} /> : null}
            {c.row.original.associationStatus === 'PROVISIONAL' ? <Bot className="size-3.5 shrink-0 text-warning" aria-label={t('status.association.PROVISIONAL')} /> : null}
            <span className="truncate font-medium text-fg">{c.getValue()}</span>
            {(columnVisibility.labels === false ? c.row.original.labels.slice(0, 2) : []).map((l) => (
              <Badge key={l} tone="neutral" className="hidden xl:inline-flex">
                {l}
              </Badge>
            ))}
          </div>
        ),
      }),
      col.accessor('phase', { header: t('work.columns.phase'), cell: (c) => <PhaseBadge phase={c.getValue()} /> }),
      col.accessor('priority', { header: t('work.columns.priority'), cell: (c) => <PriorityLabel priority={c.getValue()} /> }),
      col.accessor('type', { header: t('work.columns.type'), cell: (c) => <span className="text-xs text-fg-muted">{t(`status.workType.${c.getValue()}`)}</span> }),
      col.accessor('assignee', { header: t('work.columns.assignee'), cell: (c) => <UserChip user={c.getValue()} /> }),
      col.accessor('owner', { header: t('work.columns.owner'), cell: (c) => <UserChip user={c.getValue()} /> }),
      col.display({ id: 'scope', header: t('work.columns.project'), cell: ({ row }) => <span className="truncate text-xs text-fg-muted">{row.original.project?.name ?? row.original.team?.name ?? '—'}</span> }),
      col.display({ id: 'team', header: t('work.columns.team'), cell: ({ row }) => <span className="truncate text-xs text-fg-muted">{row.original.team?.name ?? '—'}</span> }),
      col.display({
        id: 'labels',
        header: t('work.columns.labels'),
        cell: ({ row }) => (
          <span className="flex gap-1">
            {row.original.labels.slice(0, 3).map((l) => (
              <Badge key={l} tone="neutral">
                {l}
              </Badge>
            ))}
          </span>
        ),
      }),
      col.display({ id: 'delivery', header: t('work.columns.delivery'), cell: ({ row }) => (row.original.completionPolicy === 'PRODUCTION' && row.original.phase !== 'TODO' ? <DeliveryBadge state={row.original.deliveryState} /> : null) }),
      col.accessor('createdAt', { header: t('work.columns.created'), cell: (c) => <span className="text-xs"><TimeAgo iso={c.getValue()} /></span> }),
      col.accessor('updatedAt', { header: t('work.columns.updated'), cell: (c) => <span className="text-xs"><TimeAgo iso={c.getValue()} /></span> }),
    ],
    [t, columnVisibility],
  );
  const table = useReactTable({
    data: items,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (r) => r.id,
    state: { rowSelection: selection, columnVisibility },
    onRowSelectionChange: setSelection,
    enableRowSelection: true,
  });
  const selected = items.filter((i) => selection[i.id]);
  return (
    <>
      {selected.length ? <BulkBar items={selected} onDone={() => setSelection({})} /> : null}
      <ul className="divide-y divide-border md:hidden">
        {items.map((w) => (
          <li key={w.id}>
            <button type="button" data-testid="work-row" onClick={() => onOpen(w.id)} className="flex w-full flex-col gap-1.5 bg-surface px-4 py-3 text-left">
              <span className="flex items-center gap-2">
                <KeyLabel value={w.key} />
                {w.blocked ? <AlertOctagon className="size-3.5 text-danger" aria-label={t('work.detail.blockedNote')} /> : null}
                <span className="ml-auto text-xs"><TimeAgo iso={w.updatedAt} /></span>
              </span>
              <span className="text-sm font-medium text-fg">{w.title}</span>
              <span className="flex flex-wrap items-center gap-2">
                <PhaseBadge phase={w.phase} />
                <PriorityLabel priority={w.priority} />
                {w.assignee ? <UserChip user={w.assignee} /> : null}
              </span>
            </button>
          </li>
        ))}
      </ul>
      <table className="hidden w-full border-collapse text-sm md:table">
        <thead className="sticky top-0 z-10 bg-surface-2">
          {table.getHeaderGroups().map((hg) => (
            <tr key={hg.id} className="border-b border-border">
              {hg.headers.map((h) => (
                <th key={h.id} scope="col" className={cn('px-3 py-2 text-left text-xs font-medium text-fg-subtle', h.id === 'select' && 'w-8', ['delivery', 'scope'].includes(h.id) && 'hidden lg:table-cell', h.id === 'assignee' && 'hidden md:table-cell')}>
                  {flexRender(h.column.columnDef.header, h.getContext())}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => (
            <tr
              key={row.id}
              data-testid="work-row"
              tabIndex={0}
              onClick={() => onOpen(row.original.id)}
              onKeyDown={(e) => e.key === 'Enter' && onOpen(row.original.id)}
              className={cn('cursor-pointer border-b border-border bg-surface hover:bg-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring', selectedId === row.original.id && 'bg-accent-soft/30')}
              aria-selected={selectedId === row.original.id}
            >
              {row.getVisibleCells().map((cell) => (
                <td key={cell.id} className={cn('max-w-0 px-3 py-2', cell.column.id === 'title' ? 'w-full' : 'whitespace-nowrap', ['delivery', 'scope'].includes(cell.column.id) && 'hidden lg:table-cell', cell.column.id === 'assignee' && 'hidden md:table-cell', cell.column.id !== 'title' && 'max-w-none')}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}

/** Atomic bulk change (max 100); a hidden or conflicting item fails the whole command. */
function BulkBar({ items, onDone }: { items: WorkItemListItem[]; onDone: () => void }) {
  const { t } = useTranslation();
  const bulk = useApiMutation('bulkUpdateWork', { invalidate: ['listWorkItems', 'getWorkItem'] });
  const projects = useApiQuery('listProjects', { query: {} });
  const teams = useApiQuery('listTeams', { query: {} });
  const [label, setLabel] = useState('');
  const present = [...new Set(items.flatMap((i) => i.labels))].sort();
  const allArchived = items.every((i) => i.archivedAt);
  type Patch = { priority?: 'URGENT' | 'HIGH' | 'NORMAL' | 'LOW'; assigneeId?: string | null; labelsAdd?: string[]; labelsRemove?: string[]; projectId?: string | null; teamId?: string; archived?: boolean };
  const run = async (patch: Patch) => {
    try {
      await bulk.mutateAsync({ idempotencyKey: newIdempotencyKey(), body: { items: items.map((i) => ({ id: i.id, expectedVersion: i.version })), ...patch } });
      toast.success(t('common.changesSaved'));
      setLabel('');
      onDone();
    } catch (error) {
      toastError(error);
    }
  };
  const select = 'h-7 w-auto';
  return (
    <div className="sticky top-0 z-20 flex flex-wrap items-center gap-2 border-b border-accent/40 bg-accent-soft px-3 py-2 text-sm text-accent-soft-fg">
      <span className="font-medium">{t('work.bulk.selected', { count: items.length })}</span>
      {allArchived ? null : (
        <>
          <NativeSelect className={select} value="" onChange={(e) => e.target.value && void run({ priority: e.target.value as Patch['priority'] })} aria-label={t('work.bulk.setPriority')}>
            <option value="">{t('work.bulk.setPriority')}</option>
            {(['URGENT', 'HIGH', 'NORMAL', 'LOW'] as const).map((p) => (
              <option key={p} value={p}>
                {t(`status.priority.${p}`)}
              </option>
            ))}
          </NativeSelect>
          <div className="w-44">
            <PrincipalPicker label={t('work.bulk.assign')} value={null} placeholder={t('work.bulk.assign')} onChange={(p) => void run({ assigneeId: p?.id ?? null })} />
          </div>
          <NativeSelect className={select} value="" onChange={(e) => e.target.value && void run({ projectId: e.target.value === 'none' ? null : e.target.value })} aria-label={t('work.bulk.setProject')}>
            <option value="">{t('work.bulk.setProject')}</option>
            <option value="none">{t('work.bulk.noProject')}</option>
            {(projects.data?.data ?? []).map((p) => (
              <option key={p.id} value={p.id}>
                {p.key} · {p.name}
              </option>
            ))}
          </NativeSelect>
          <NativeSelect className={select} value="" onChange={(e) => e.target.value && void run({ teamId: e.target.value })} aria-label={t('work.bulk.setTeam')}>
            <option value="">{t('work.bulk.setTeam')}</option>
            {(teams.data?.data ?? []).map((team) => (
              <option key={team.id} value={team.id}>
                {team.name}
              </option>
            ))}
          </NativeSelect>
          <form
            className="flex items-center gap-1"
            onSubmit={(e) => {
              e.preventDefault();
              if (label.trim()) void run({ labelsAdd: [label.trim()] });
            }}
          >
            <Input className="h-7 w-28" value={label} onChange={(e) => setLabel(e.target.value)} maxLength={40} placeholder={t('work.bulk.labelPlaceholder')} aria-label={t('work.bulk.addLabel')} />
            <Button type="submit" size="sm" variant="secondary" disabled={!label.trim()}>
              <Plus /> {t('work.bulk.addLabel')}
            </Button>
          </form>
          {present.length ? (
            <NativeSelect className={select} value="" onChange={(e) => e.target.value && void run({ labelsRemove: [e.target.value] })} aria-label={t('work.bulk.removeLabel')}>
              <option value="">{t('work.bulk.removeLabel')}</option>
              {present.map((l) => (
                <option key={l} value={l}>
                  {l}
                </option>
              ))}
            </NativeSelect>
          ) : null}
        </>
      )}
      {allArchived ? (
        <Button size="sm" variant="secondary" loading={bulk.isPending} onClick={() => void run({ archived: false })}>
          <ArchiveRestore /> {t('work.bulk.restore')}
        </Button>
      ) : items.every((i) => i.phase === 'DONE' || i.phase === 'CANCELED') ? (
        <Button size="sm" variant="secondary" loading={bulk.isPending} onClick={() => void run({ archived: true })}>
          <Archive /> {t('work.bulk.archive')}
        </Button>
      ) : null}
      <Button size="sm" variant="ghost" onClick={onDone} className="ml-auto">
        {t('work.bulk.clear')}
      </Button>
    </div>
  );
}
